/**
 * "Not able to recommend interviewers" banner.
 *
 * Appears below the interviewer field when the wizard would otherwise
 * have shown the AI-recommendation pill but the conditions for
 * recommending aren't met. Two distinct cases share one component so
 * the styling stays consistent (heading + subheading + sad icon):
 *
 *   - `empty-plan`        — the interview plan has no interviewers AND
 *                          no interviewer pool. Nothing to recommend
 *                          from. Copy nudges the user to set up the
 *                          plan first.
 *   - `no-recommendation` — the suggestion API ran and returned null
 *                          (couldn't pick anyone given current load /
 *                          availability). Copy nudges the user to
 *                          either pick a slot or choose manually.
 *
 * Ports the legacy `SchInterviewerFieldNoRecommend` behaviour from the
 * old KInterviewScheduler.
 */
import { Box, Flex, Text } from '@chakra-ui/react';
import { FaceFrown } from '@kula-ai/icons-core';

export type CannotRecommendReason = 'empty-plan' | 'no-recommendation';

interface CannotRecommendBannerProps {
  reason: CannotRecommendReason;
}

const COPY: Record<CannotRecommendReason, { heading: string; body: string }> = {
  'empty-plan': {
    heading: 'Not able to recommend interviewers',
    body: 'No interviewers or interviewer pool have been selected in the interview plan.',
  },
  'no-recommendation': {
    heading: 'No interviewer recommendations',
    body: 'We could not recommend interviewers based on the current interview plan. Try selecting a slot or choose manually.',
  },
};

export const CannotRecommendBanner = ({
  reason,
}: CannotRecommendBannerProps) => {
  const { heading, body } = COPY[reason];
  return (
    <Box
      bg="secondary-50"
      borderRadius="md"
      p="3"
      // Pull up to sit flush under the interviewer field, matching the
      // existing InterviewerStatusBanner spacing.
      mt="-2"
    >
      <Flex flexDir="column" gap="2">
        <Flex align="center" gap="2">
          <FaceFrown
            width={20}
            height={20}
            color="var(--kula-colors-secondary-700)"
          />
          <Text fontSize="sm" fontWeight="semibold" color="secondary-900">
            {heading}
          </Text>
        </Flex>
        <Text fontSize="sm" color="secondary-700">
          {body}
        </Text>
      </Flex>
    </Box>
  );
};
